import { Link } from 'react-router-dom';
import { careerArc, getProduct } from '../../data/products';

/* The arc and the platforms are one section: the gold line is the career,
   the cards are the products stationed along it, and Express deliberately
   sits outside the arc, because it is the tools without the content. */

function thumbFor(slug) {
  const p = getProduct(slug);
  const shots = p && p.media && p.media.screenshots;
  return shots && shots.length > 0 ? shots[0].src : null;
}

export default function CareerArc() {
  const arcProducts = careerArc.flatMap((s) => s.slugs).map(getProduct).filter(Boolean);
  const express = getProduct('express');

  return (
    <div>
      {/* The line. Drawn once on load: the site's single orchestrated motion. */}
      <div className="hidden md:block" aria-hidden="true">
        <svg viewBox="0 0 1000 60" className="w-full" role="presentation">
          <path
            d="M 10 40 C 200 10, 300 50, 500 30 S 800 10, 990 35"
            fill="none"
            stroke="#2dd4bf"
            strokeWidth="2"
            className="arc-draw"
          />
          {[10, 255, 500, 745, 990].map((x, i) => {
            const ys = [40, 27, 30, 21, 35];
            return <circle key={x} cx={x} cy={ys[i]} r="5" fill="#13273F" stroke="#2dd4bf" strokeWidth="2" />;
          })}
        </svg>
      </div>

      {/* The stages. */}
      <ol className="grid gap-6 md:grid-cols-5 md:gap-4">
        {careerArc.map((step) => (
          <li key={step.stage} className="relative border-l border-hairline pl-4 md:border-l-0 md:pl-0">
            <span
              aria-hidden="true"
              className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-seal bg-ink md:hidden"
            />
            <p className="text-sm text-slate">{step.stage}</p>
            <p className="mt-1 font-serif text-lg">{step.label}</p>
          </li>
        ))}
      </ol>

      {/* The platforms stationed along it. */}
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {arcProducts.map((p) => {
          const thumb = thumbFor(p.slug);
          return (
            <Link key={p.slug} to={`/products/${p.slug}`} className="platform-card group">
              {thumb ? (
                <div className="platform-thumb">
                  <img src={thumb} alt="" loading="lazy" />
                </div>
              ) : (
                <div className="platform-thumb platform-thumb-empty">
                  <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[1px] bg-seal" />
                </div>
              )}
              <div className="p-5">
                <p className="text-xs text-slate">{p.audienceTag}</p>
                <p className="mt-1 font-serif text-lg leading-snug">{p.name}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate">{p.headline}</p>
                <p className="mt-4 text-sm font-semibold text-bright underline decoration-hairline underline-offset-4 transition-colors group-hover:decoration-seal">
                  Explore {p.name}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Express, outside the arc on purpose. */}
      {express && (
        <Link
          to="/products/express"
          className="group mt-6 flex flex-col gap-4 rounded-media border border-dashed border-seal/50 bg-paper p-6 transition-colors hover:border-seal md:flex-row md:items-center md:justify-between md:px-8"
        >
          <div>
            <p className="text-xs text-slate">And outside the arc entirely</p>
            <p className="mt-1 font-serif text-lg">
              {express.name}
              <span className="text-slate">: the tools without the content.</span>
            </p>
            <p className="mt-1 max-w-measure-wide text-sm leading-relaxed text-slate">
              The same modality engine, decoupled from the corpus. Your own material in, exam-tuned
              study tools out. Any learner, any domain.
            </p>
          </div>
          <p className="shrink-0 text-sm font-semibold text-bright underline decoration-hairline underline-offset-4 transition-colors group-hover:decoration-seal">
            Explore {express.name}
          </p>
        </Link>
      )}

      <p className="mt-10 max-w-measure font-serif italic text-slate">
        One corpus. One company. The whole arc.
      </p>
    </div>
  );
}
